/**
 * The screen's side of the one rule for values the platform fills on its own (S 2.7; the server's is Ncp.Application.AutomaticFill): a value
 * read from a document, a refetch or a default reaches a field only while the field is empty, and a field the person has written in keeps
 * what they wrote. Free of React so the rules are tested on their own (scripts/dev/test/automatic-fill.test.mjs).
 */

/** Whether a value holds nothing a person entered: absent, blank text, an empty list or an empty object. Zero and false are values. */
export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

/**
 * Plans an automatic fill: each value goes into its field only while the field is empty (`isEmpty`); a field that holds a value – or that
 * an earlier value of the same fill has just filled – keeps it, and the value is offered for the person to take by hand.
 */
export function planFill<S>(items: readonly S[], keyOf: (item: S) => string, isEmpty: (item: S) => boolean): { fill: S[]; offer: S[] } {
  const fill: S[] = [];
  const offer: S[] = [];
  const taken = new Set<string>();
  for (const item of items) {
    const key = keyOf(item);
    if (!taken.has(key) && isEmpty(item)) {
      taken.add(key);
      fill.push(item);
    } else offer.push(item);
  }
  return { fill, offer };
}

const isPlain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * The server's new values for a form the person may be writing in: a field that still holds what the server sent last takes the new value;
 * a field the person changed keeps their text. `last` is what the server sent before, `next` what it sends now. Nested records (one entry
 * per document, per language) are taken field by field; a list is one value, kept whole once the person changed it.
 */
export function keepEdited<T>(current: T, last: T, next: T): T {
  if (!isPlain(current) || !isPlain(next)) return sameValue(current, last) ? next : current;
  const merged: Record<string, unknown> = { ...current };
  const was: Record<string, unknown> = isPlain(last) ? last : {};
  for (const [key, value] of Object.entries(next)) {
    merged[key] = isPlain(value) && isPlain(merged[key]) ? keepEdited(merged[key], was[key] ?? {}, value) : sameValue(merged[key], was[key]) ? value : merged[key];
  }
  return merged as T;
}

/** Equal as the person sees them: the same text, number or list. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isBlank(a) && isBlank(b)) return true;
  if (typeof a === "object" && typeof b === "object" && a && b) return JSON.stringify(a) === JSON.stringify(b);
  return false;
}
